/* ==========================================================================
   Flooring Attic — Mobile Menu Drawer helpers
   Two features, one observer:
     1. Wishlist heart + count badge on the WISHLIST link
        Source: localStorage.fa_wishlist (same as desktop nav)
     2. Real product counts on collection menu items
        Source: /collections/{handle}.json (Shopify storefront API)
        Cached per-session in sessionStorage so we don't refetch on
        every page navigation.
   Uses MutationObserver so both survive any re-render of the header
   (Section Rendering API, custom-element hydration, theme editor refresh).
   ========================================================================== */
(function () {
  if (window.__faMenuDrawerObs) return; // idempotent — don't attach twice

  // ────────────────────────── 1) WISHLIST INDICATOR ──────────────────────────

  function getWishlistCount() {
    try {
      var raw = localStorage.getItem('fa_wishlist');
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.length : 0;
    } catch (e) { return 0; }
  }

  function buildWishlistIndicator(count) {
    var wrap = document.createElement('span');
    wrap.className = 'fa-menu-wl-indicator';

    var heart = document.createElement('span');
    heart.className = 'fa-menu-wl-heart';
    heart.innerHTML =
      '<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.8" aria-hidden="true">' +
        '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>' +
      '</svg>';
    wrap.appendChild(heart);

    var badge = document.createElement('span');
    badge.className = 'fa-menu-wl-count' + (count === 0 ? ' fa-menu-wl-count--zero' : '');
    badge.textContent = String(count);
    wrap.appendChild(badge);

    return wrap;
  }

  function ensureWishlistIndicator() {
    var drawer = document.querySelector('.header__drawer--mobile');
    if (!drawer) return;
    var wl = drawer.querySelector('a[href*="/pages/wishlist"]');
    if (!wl) return;
    if (wl.querySelector('.fa-menu-wl-indicator')) return;
    wl.appendChild(buildWishlistIndicator(getWishlistCount()));
  }

  // ────────────────────────── 2) PRODUCT COUNTS ──────────────────────────────

  // sessionStorage cache so we don't refetch on every page load
  var CACHE_KEY = '__fa_collection_counts';

  function getCache() {
    try { return JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}'); }
    catch (e) { return {}; }
  }

  function setCache(obj) {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(obj)); }
    catch (e) {}
  }

  // Pull the collection handle out of a /collections/{handle} URL.
  // Returns null for "all" (used by View All) and non-collection links.
  function extractHandle(href) {
    if (!href) return null;
    var m = href.match(/\/collections\/([^/?#]+)/);
    if (!m) return null;
    if (m[1] === 'all') return null;
    return m[1];
  }

  // Fill product counts on every eligible submenu collection link.
  // Skips: View All (.fa-submenu-viewall), non-collection URLs, items that
  // already have a count rendered server-side or by a previous tick.
  function fillProductCounts() {
    var items = document.querySelectorAll(
      '.header__drawer--mobile .menu-drawer__menu-item--child'
    );
    var cache = getCache();
    var toFetch = []; // [{ span, handle }]

    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (it.classList.contains('fa-submenu-viewall')) continue;
      var handle = extractHandle(it.getAttribute('href'));
      if (!handle) continue;
      var titleSpan = it.querySelector('.menu-drawer__menu-item-text');
      if (!titleSpan) continue;
      if (titleSpan.querySelector('.fa-menu-product-count')) continue;

      // If we have a cached value, render it synchronously
      if (typeof cache[handle] === 'number') {
        var span = document.createElement('span');
        span.className = 'fa-menu-product-count';
        span.textContent = cache[handle] + ' products';
        titleSpan.appendChild(span);
        continue;
      }

      // Otherwise insert a placeholder and queue the fetch
      var placeholder = document.createElement('span');
      placeholder.className = 'fa-menu-product-count fa-loading';
      placeholder.textContent = '… products';
      titleSpan.appendChild(placeholder);
      toFetch.push({ span: placeholder, handle: handle });
    }

    if (toFetch.length === 0) return;

    Promise.all(toFetch.map(function (entry) {
      return fetch('/collections/' + entry.handle + '.json')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          var count = d && d.collection && typeof d.collection.products_count === 'number'
            ? d.collection.products_count
            : 0;
          entry.span.textContent = count + ' products';
          entry.span.classList.remove('fa-loading');
          cache[entry.handle] = count;
        })
        .catch(function () {
          entry.span.textContent = '0 products';
          entry.span.classList.remove('fa-loading');
        });
    })).then(function () { setCache(cache); });
  }

  // ────────────────────────── DRIVER ────────────────────────────────────────

  function tick() {
    ensureWishlistIndicator();
    fillProductCounts();
  }

  // Run immediately
  tick();

  // Re-run on any DOM change below <body>. Survives any re-render
  // (Section Rendering API, custom-element hydration, theme editor refresh).
  var obs = new MutationObserver(tick);
  obs.observe(document.body, { childList: true, subtree: true });
  window.__faMenuDrawerObs = obs;

  // Cross-tab sync — when the wishlist changes in another tab/window,
  // refresh the badge here.
  window.addEventListener('storage', function (e) {
    if (e.key !== 'fa_wishlist') return;
    var wl = document.querySelector('.header__drawer--mobile a[href*="/pages/wishlist"]');
    if (!wl) return;
    var existing = wl.querySelector('.fa-menu-wl-indicator');
    if (existing) existing.remove();
    wl.appendChild(buildWishlistIndicator(getWishlistCount()));
  });

  // Same-tab refresh helper — call this from any wishlist add/remove handler.
  window.FaMenuRefreshWishlist = function () {
    var wl = document.querySelector('.header__drawer--mobile a[href*="/pages/wishlist"]');
    if (!wl) return;
    var existing = wl.querySelector('.fa-menu-wl-indicator');
    if (existing) existing.remove();
    wl.appendChild(buildWishlistIndicator(getWishlistCount()));
  };

  // Same-tab refresh helper for product counts — call after a collection
  // changes, or pass a specific handle to invalidate just that one.
  window.FaMenuRefreshCounts = function (handle) {
    var cache = getCache();
    if (handle) { delete cache[handle]; } else { cache = {}; }
    setCache(cache);
    // Strip rendered counts so fillProductCounts() will repopulate
    document.querySelectorAll(
      '.header__drawer--mobile .fa-menu-product-count'
    ).forEach(function (el) { el.remove(); });
    fillProductCounts();
  };
})();